import { PartialPartner } from "@/common/partner/types";
import Icon from "@/components/icons";
import { useProgramRedesignConfig } from "@/monarch/rules/programRedesignTraffic/useProgramRedesignTraffic";

interface VerifiedBadgeProps {
  partners: PartialPartner[];
  className?: string;
  iconClassName?: string;
  textClassName?: string;
  verifiedBadgeConfig?:
    | {
        icon: string;
        displayPartnerIdentifier: boolean;
        displayNameOrKey: "name" | "key";
        text: string;
      }
    | undefined;
}

const formatPartnerNames = (
  partners: PartialPartner[],
  text?: string,
  displayNameOrKey?: "name" | "key",
): string => {
  const displayName = displayNameOrKey === "name";
  if (partners.length === 0) return "";
  if (partners.length === 1)
    return `${displayName ? partners[0].name : partners[0].key} ${text || "verified"}`;
  if (partners.length === 2)
    return `${displayName ? partners[0].name : partners[0].key} and ${displayName ? partners[1].name : partners[1].key} ${text || "verified"}`;

  // For 3 or more partners: "Partner 1, Partner 2, and Partner 3 Verified"
  const allButLast = partners
    .slice(0, -1)
    .map((partner) => (displayName ? partner.name : partner.key))
    .join(", ");
  const lastPartner = displayName
    ? partners[partners.length - 1].name
    : partners[partners.length - 1].key;
  return `${allButLast}, and ${lastPartner} ${text || "verified"}`;
};

export default function VerifiedBadge({
  partners,
  className = "",
  textClassName = "",
  verifiedBadgeConfig,
}: VerifiedBadgeProps) {
  const { icon, displayPartnerIdentifier, text, displayNameOrKey } =
    verifiedBadgeConfig || {};
  if (!partners || partners.length === 0) return null;

  const formattedText = formatPartnerNames(partners, text, displayNameOrKey);

  return (
    <div
      className={`inline-flex items-center gap-1 rounded border-[1.5px] py-1 px-2 bg-accent-pink ${className}`}
    >
      <Icon type={icon || "certificate-ribbon"} />
      <span className={`text-sm font-semibold ${textClassName}`}>
        {verifiedBadgeConfig
          ? displayPartnerIdentifier
            ? formattedText
            : text
          : formattedText}
      </span>
    </div>
  );
}
